import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSeo from '@/hooks/useSeo';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import SectionHeading from '@/components/base/SectionHeading';
import EventCard from '@/components/base/EventCard';
import { events } from '@/mocks/events';
import { categories } from '@/mocks/categories';

const SITE_URL = 'https://captain2016.cn';

type SortKey = 'heat' | 'date';

export default function TimelineList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const category = searchParams.get('category') ?? 'all';
  const [keyword, setKeyword] = useState(q);
  const [sort, setSort] = useState<SortKey>('heat');

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  const filtered = useMemo(() => {
    const kw = q.trim().toLowerCase();
    let list = [...events];
    if (category !== 'all') {
      list = list.filter((item) => item.category === category);
    }
    if (kw) {
      list = list.filter((item) =>
        `${item.title}${item.summary}${item.categoryName}${item.tags.join('')}`
          .toLowerCase()
          .includes(kw),
      );
    }
    list.sort((a, b) => (sort === 'heat' ? b.heat - a.heat : a.date < b.date ? 1 : -1));
    return list;
  }, [q, category, sort]);

  const activeCategory = categories.find((item) => item.id === category);

  useSeo({
    title: activeCategory
      ? `${activeCategory.name}时间线合集 — 蝌蚪吃瓜事件复盘`
      : '娱乐事件时间线合集 — 蝌蚪吃瓜来龙去脉完整整理',
    description: activeCategory
      ? `蝌蚪吃瓜${activeCategory.name}栏目合集，${activeCategory.description} 按更新时间与热度整理，快速看清来龙去脉。`
      : '蝌蚪吃瓜事件合集，收录明星动向、影视综、音乐现场、饭圈舆论等话题的时间线复盘，支持关键词搜索与分类筛选，前因后果一览无余。',
    keywords: '蝌蚪吃瓜,事件合集,娱乐时间线,来龙去脉,前因后果深度拆解',
    path: category === 'all' ? '/timeline' : `/timeline?category=${category}`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: activeCategory ? `${activeCategory.name}时间线合集` : '娱乐事件时间线合集',
        url: `${SITE_URL}/timeline`,
        description: '蝌蚪吃瓜事件合集，收录各类娱乐话题时间线复盘。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: '事件合集', item: `${SITE_URL}/timeline` },
        ],
      },
    ],
  });

  const updateParams = (next: Record<string, string>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
    setSearchParams(params);
  };

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateParams({ q: keyword.trim() });
  };

  return (
    <div className="bg-background-50">
      <section className="border-b border-background-200 bg-background-100 py-10 md:py-14">
        <div className="mx-auto w-full px-4 md:px-6">
          <Breadcrumbs
            items={[{ label: '事件合集', to: activeCategory ? '/timeline' : undefined }, ...(activeCategory ? [{ label: activeCategory.name }] : [])]}
          />
          <div className="mt-5">
            <SectionHeading
              kicker={activeCategory ? '栏目合集' : '全部事件'}
              title={activeCategory ? `${activeCategory.name}时间线` : '娱乐事件时间线合集'}
              description={
                activeCategory
                  ? activeCategory.description
                  : '所有已梳理完成的事件都在这里。可以用关键词搜索，也可以按栏目筛选，排序支持热度优先与最新更新。'
              }
            />
          </div>
        </div>
      </section>

      <section className="py-8 md:py-10">
        <div className="mx-auto w-full px-4 md:px-6">
          <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-center md:justify-between">
            <form onSubmit={handleSearch} role="search" className="flex w-full items-center gap-2 md:max-w-md">
              <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
                <i className="ri-search-line text-foreground-500" />
                <label htmlFor="list-search" className="sr-only">
                  搜索事件关键词
                </label>
                <input
                  id="list-search"
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索事件、人物或关键词"
                  className="w-full bg-transparent py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                />
              </div>
              <button
                type="submit"
                className="shrink-0 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </form>

            <div className="flex flex-wrap items-center gap-2">
              <span className="font-label text-xs text-foreground-500">排序</span>
              {([
                { key: 'heat', label: '热度优先', icon: 'ri-fire-line' },
                { key: 'date', label: '最新更新', icon: 'ri-time-line' },
              ] as const).map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSort(option.key)}
                  className={`flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 font-label text-sm transition-colors ${
                    sort === option.key
                      ? 'bg-foreground-950 text-background-50'
                      : 'border border-background-300 text-foreground-700 hover:border-primary-300'
                  }`}
                >
                  <i className={option.icon} />
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => updateParams({ category: '' })}
              className={`rounded-full px-3.5 py-1.5 font-label text-sm transition-colors ${
                category === 'all'
                  ? 'bg-primary-500 text-background-50'
                  : 'border border-background-300 text-foreground-700 hover:border-primary-300'
              }`}
            >
              全部
            </button>
            {categories.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => updateParams({ category: item.id })}
                className={`rounded-full px-3.5 py-1.5 font-label text-sm transition-colors ${
                  category === item.id
                    ? 'bg-primary-500 text-background-50'
                    : 'border border-background-300 text-foreground-700 hover:border-primary-300'
                }`}
              >
                {item.name}
                <span className="ml-1 text-xs opacity-70">{item.count}</span>
              </button>
            ))}
          </div>

          <p className="mt-6 font-label text-sm text-foreground-500">
            共找到 <span className="text-primary-600">{filtered.length}</span> 条时间线
            {q && <span> · 关键词「{q}」</span>}
          </p>

          {filtered.length > 0 ? (
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((item) => (
                <EventCard key={item.id} event={item} />
              ))}
            </div>
          ) : (
            <div className="mt-6 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-search-eye-line text-2xl" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">没有找到匹配的时间线</h3>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                换个关键词试试，或者清空筛选条件查看全部事件。也可以把线索发给我们，编辑会优先梳理。
              </p>
              <button
                type="button"
                onClick={() => {
                  setKeyword('');
                  setSearchParams(new URLSearchParams());
                }}
                className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}